import { Siren, TrendingDown } from "lucide-react";
import { fmtMoney } from "@/lib/format";

// One flashing "police-light" card holding BOTH pricing red flags: mandatory fees that Ontario's all-in
// law requires inside the advertised price, and optional add-ons the buyer said they never asked for.
export const PriceFlagsCard = ({ result, declinedAddons = [] }) => {
  const fees = result.illegal_addons || [];
  const hasFees = fees.length > 0;
  const hasAddons = declinedAddons.length > 0;
  if (!hasFees && !hasAddons) return null;

  return (
    <div
      data-testid="price-flags-card"
      className="rounded-xl border-2 border-[#dc2626] bg-card p-6 shadow-[0_0_22px_-6px_rgba(220,38,38,0.5)]"
    >
      {hasFees && (
        <div data-testid="illegal-addons">
          <div className="flex items-center gap-2">
            <Siren className="h-5 w-5 animate-police-text" aria-hidden="true" />
            <h3 className="font-heading text-lg font-800 uppercase tracking-wide animate-police-text">
              Fees that must be included in the advertised price
            </h3>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Under Ontario's all-in price advertising law (Motor Vehicle Dealers Act, 2002,
            O. Reg. 333/08, s. 36), a dealer's advertised price must be the total amount a consumer is
            required to pay, including all mandatory fees and charges the dealer intends to collect. The
            only amounts that may be added on top are HST and licensing (the actual registration/plate
            cost). The following charges must already be included in the advertised price:
          </p>
          <ul className="mt-3 space-y-1.5">
            {fees.map((a, i) => (
              <li key={`${a.label}-${i}`} className="flex items-center justify-between text-sm">
                <span className="text-foreground">{a.label}</span>
                <span className="font-mono-plex font-600 text-[#ef4444]">{fmtMoney(a.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasFees && hasAddons && <div className="my-6 h-px w-full bg-[#dc2626]/30" />}

      {hasAddons && (
        <div data-testid="optional-addons-warning">
          <div className="flex items-center gap-2">
            <Siren className="h-5 w-5 animate-police-text" aria-hidden="true" />
            <h3 className="font-heading text-lg font-800 uppercase tracking-wide animate-police-text">
              Add-ons you didn't ask for
            </h3>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            You told us you didn't request these. They're <span className="font-600 text-foreground">not part
            of the vehicle price</span> and don't need to be on the contract — ask the dealer to remove them.
          </p>

          <ul className="mt-3 space-y-1.5">
            {declinedAddons.map((a, i) => (
              <li
                key={`${a.label}-${a.category}-${i}`}
                data-testid={`addon-${i}`}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-foreground">{a.label}</span>
                <span className="font-mono-plex font-600 text-[#ef4444]">{fmtMoney(a.amount)}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 overflow-hidden rounded-lg border border-primary/30 border-l-[3px] border-l-primary bg-primary/[0.06] p-4">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <h4 className="font-mono-plex text-[11px] font-700 uppercase tracking-[0.18em] text-primary">
                Negotiation reminder
              </h4>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground/90">
              Start from the <span className="font-700 text-primary">lowest advertised all-in price</span> for
              this exact vehicle — <span className="font-700 text-primary">without any warranty, protection
              package, insurance, or accessory</span> added on top. Add these back only if you genuinely want
              them, at a price you choose.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
